import { Injectable, inject } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { HELP_TOPIC_REGISTRY, type HelpTopicId } from '@zag/ai/models';
import { HelpDialogComponent, type HelpDialogData } from './help-dialog.component';

/**
 * Opens the stepped help dialog. Prefer {@link HelpDialogService.openTopic}
 * when a topic ID is available — it ensures the same step count and title
 * key used by the chat assistant and the in-app help icons.
 */
@Injectable({ providedIn: 'root' })
export class HelpDialogService {
  private readonly dialog = inject(Dialog);

  open(data: HelpDialogData): void {
    this.dialog.open<void, HelpDialogData>(HelpDialogComponent, {
      data,
      panelClass: 'ai-chat-dialog-panel',
      backdropClass: 'ai-chat-dialog-backdrop',
      autoFocus: 'dialog',
      restoreFocus: true,
    });
  }

  /**
   * Open the dialog for a known help topic, using the shared registry to
   * resolve titleKey + step count. This is the recommended entry point —
   * call sites that already know the topic ID (e.g. settings pages, AI chat
   * inline help links) should use this so the dialog and chat always stay
   * in sync with the i18n bundle.
   */
  openTopic(topicId: HelpTopicId): void {
    const meta = HELP_TOPIC_REGISTRY[topicId];
    this.open({
      titleKey: meta.titleKey,
      icon: meta.icon,
      steps: buildHelpSteps(meta.baseKey, meta.stepCount),
    });
  }
}

/**
 * Build the conventional `<baseKey>.s<N>.{title,body}` step list. Prefer
 * passing topic IDs through {@link HelpDialogService.openTopic} so the
 * registry stays the single source of truth for step counts.
 *
 * @example
 * buildHelpSteps('helpDialog.socialLogin.microsoft', 6)
 * // → six entries with titleKey `helpDialog.socialLogin.microsoft.s1.title` …
 */
export function buildHelpSteps(baseKey: string, count: number): { titleKey: string; bodyKey: string }[] {
  return Array.from({ length: count }, (_, i) => ({
    titleKey: `${baseKey}.s${i + 1}.title`,
    bodyKey: `${baseKey}.s${i + 1}.body`,
  }));
}
